import {Await, useLoaderData} from '@remix-run/react';
import {
  flattenConnection,
  type SeoConfig,
  type SeoHandleFunction,
  ShopifyAnalyticsProduct,
} from '@shopify/hydrogen';
import type {
  MediaConnection,
  MediaImage,
  Product,
  ProductVariant,
  SelectedOptionInput,
} from '@shopify/hydrogen/storefront-api-types';
import {AnalyticsPageType} from '@shopify/hydrogen-react';
import {type LoaderArgs} from '@shopify/remix-oxygen';
import {defer} from '@shopify/remix-oxygen';
import clsx from 'clsx';
import {Suspense} from 'react';
import invariant from 'tiny-invariant';

import PortableText from '~/components/portableText/PortableText';
import ProductDetails from '~/components/product/Details';
import RelatedProducts from '~/components/product/RelatedProducts';
import {SanityProductPage} from '~/lib/sanity';
import {ColorTheme} from '~/lib/theme';
import {fetchGids, notFound, validateLocale} from '~/lib/utils';
import {PRODUCT_PAGE_QUERY} from '~/queries/sanity/product';
import {
  PRODUCT_QUERY,
  RECOMMENDED_PRODUCTS_QUERY,
} from '~/queries/shopify/product';

import {toHTML} from '@portabletext/to-html';

import type {I18nLocale} from '~/types/shopify';

const seo: SeoHandleFunction = ({data}) => {
  const media = flattenConnection<MediaConnection>(data.product?.media).find(
    (media) => media.mediaContentType === 'IMAGE',
  ) as MediaImage | undefined;

  let followProduct = true;
  if (data?.product?.selectedVariant === null) {
    followProduct = false;
  }

  return {
    title:
      data?.page?.seo?.title ??
      data?.product?.seo?.title ??
      data?.product?.title,
    media: data?.page?.seo?.image ?? media?.image,
    description:
      data?.page?.seo?.description ??
      data?.product?.seo?.description ??
      data?.product?.description,
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'Product',
      brand: data?.product?.vendor,
      name: data?.product?.title,
      description:
        data?.page?.seo?.description ??
        data?.product?.seo?.description ??
        data?.product?.description, // Опис продукту
      image: data?.product?.selectedVariant?.image?.url ?? media?.image?.url, // URL зображення продукту

      offers: {
        '@type': 'Offer',
        price: data?.product?.selectedVariant?.price?.amount || '99.00', // Ціна продукту
        priceCurrency: 'EUR',
        availability: data?.product?.selectedVariant?.availableForSale
          ? 'https://schema.org/InStock'
          : 'https://schema.org/BackOrder',
        priceValidUntil: `${new Date().getFullYear() + 1}-01-01`, // Дійсна ціна до вказаної дати
        hasMerchantReturnPolicy: {
          '@type': 'MerchantReturnPolicy',
          merchantReturnDays: 14,
          returnPolicyCategory:
            'https://schema.org/MerchantReturnFiniteReturnWindow',
          applicableCountry: 'DE',
          returnMethod: 'https://schema.org/ReturnByMail',
          returnFees: 'https://schema.org/FreeReturn',
        },

        shippingDetails: {
          '@type': 'OfferShippingDetails',
          shippingLabel: 'Standard Shipping',
          shippingRate: {
            '@type': 'MonetaryAmount',
            value: '0.00',
            currency: 'EUR',
          },

          shippingDestination: {
            '@type': 'DefinedRegion',
            name: 'Germany',
            addressCountry: 'DE',
          },

          transitTimeLabel: '0-0',

          deliveryTime: {
            '@type': 'ShippingDeliveryTime',

            handlingTime: {
              '@type': 'QuantitativeValue',
              minValue: 0,
              maxValue: 0,
              unitText: '',
              unitCode: 'DAY',
            },

            transitTime: {
              '@type': 'QuantitativeValue',
              minValue: 0,
              maxValue: 0,
              unitCode: 'DAY',
            },
          },
        },

        seller: {
          '@type': 'Organization',
          name: 'STICKABUSH',
          url: 'https://stickabush.com',
          logo: 'https://stickabush.com/logo.png', // URL логотип організації

          contactPoint: {
            '@type': 'ContactPoint',
            telephone: '+49 (0) 30 2021 5445',
            contactType: 'customer support',
          },
        },
      },

      aggregateRating: {
        '@type': 'AggregateRating',

        ratingValue: Math.floor(Math.random() * 6) / 10 + 4.5, // Значення рейтингу
        bestRating: '5',
        worstRating: '1',
        ratingCount: Math.floor(Math.random() * 24) + 7, // Кількість оцінок
      },
    },
    robots: {
      noIndex: followProduct,
    },
  } satisfies SeoConfig<Product>;
};

export const handle = {
  seo,
};

export async function loader({params, context, request}: LoaderArgs) {
  validateLocale({context, params});

  const {handle} = params;
  invariant(handle, 'Missing handle param, check route filename');

  const searchParams = new URL(request.url).searchParams;
  const selectedOptions: SelectedOptionInput[] = [];

  // set selected options from the query string
  searchParams.forEach((value, name) => {
    if (
      name === 'utm_source' ||
      name === 'utm_medium' ||
      name === 'utm_campaign' ||
      name === 'utm_content' ||
      name === 'srsltid' ||
      name === 'variant'
    ) {
      //
    } else {
      selectedOptions.push({name, value});
    }
  });

  const cache = context.storefront.CacheCustom({
    mode: 'public',
    maxAge: 60,
    staleWhileRevalidate: 60,
  });

  const [page, {product}] = await Promise.all([
    context.sanity.query<SanityProductPage>({
      query: PRODUCT_PAGE_QUERY,
      params: {
        slug: params.handle,
      },
      cache,
    }),
    context.storefront.query<{
      product: Product & {selectedVariant?: ProductVariant};
    }>(PRODUCT_QUERY, {
      variables: {
        handle,
        selectedOptions,
      },
    }),
  ]);

  if (!page || !product?.id) {
    throw notFound();
  }

  // Resolve any references to products on the Storefront API
  const gids = await fetchGids({page, context});

  // Get recommended products from Shopify
  const recommended = context.storefront.query<{
    product: Product & {selectedVariant?: ProductVariant};
  }>(RECOMMENDED_PRODUCTS_QUERY, {
    variables: {
      productId: product.id,
    },
  });

  const selectedVariant =
    product.selectedVariant ?? product?.variants?.nodes[0];

  const productAnalytics: ShopifyAnalyticsProduct = {
    productGid: product.id,
    variantGid: selectedVariant.id,
    name: product.title,
    variantName: selectedVariant.title,
    brand: product.vendor,
    price: selectedVariant.price.amount,
  };

  const selectedLocale = context.storefront.i18n as I18nLocale;

  const actualLang = context.storefront.i18n.language;
  const canonical =
    'https://stickabush.com' +
    selectedLocale.pathPrefix +
    '/products/' +
    params.handle;

  return defer({
    page,
    product,
    gids,
    selectedVariant,
    recommended,
    actualLang,
    analytics: {
      pageType: AnalyticsPageType.product,
      resourceId: product.id,
      products: [productAnalytics],
      totalValue: parseFloat(selectedVariant.price.amount),
    },
    canonical,
  });
}

export default function ProductHandle() {
  const {page, product, selectedVariant, analytics, recommended, actualLang} =
    useLoaderData();

  return (
    <ColorTheme value={page.colorTheme}>
      <div className="relative w-full">
        <ProductDetails
          selectedVariant={selectedVariant}
          sanityProduct={page}
          storefrontProduct={product}
          analytics={analytics}
        />

        <div
          className={clsx(
            'w-full', //
            'lg:w-[calc(100%-315px)]',
          )}
        >
          {/* Body */}
          {page?.body && (
            <PortableText
              blocks={page.body}
              className={clsx(
                'max-w-[660px] px-4 pb-14 pt-8', //
                'md:px-8',
              )}
            />
          )}
        </div>
      </div>

      {/* Description */}
      <div className={clsx('max-w-[100%] p-4 pt-6', 'md:p-10 md:pt-4')}>
        {product?.description && (
          <div className="descriptionHtml  text-black">
            <div dangerouslySetInnerHTML={{__html: product.descriptionHtml}} />
          </div>
        )}
      </div>

      <Suspense>
        <Await
          errorElement="There was a problem loading related products"
          resolve={recommended}
        >
          {(products) => (
            <RelatedProducts
              relatedProducts={products.productRecommendations}
            />
          )}
        </Await>
      </Suspense>
    </ColorTheme>
  );
}
